const rawApiBaseUrl =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? "http://127.0.0.1:5001" : "https://assetsociety.ngo");

export const apiBaseUrl = rawApiBaseUrl.trim().replace(/\/+$/, "");

function buildApiUrl(path) {
  return `${apiBaseUrl}${path}`;
}

const CMS_TOKEN_KEY = "cms_auth_token";

export function getCmsToken() {
  return localStorage.getItem(CMS_TOKEN_KEY);
}

export function setCmsToken(token) {
  if (token) {
    localStorage.setItem(CMS_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(CMS_TOKEN_KEY);
  }
}

async function parseJsonResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

/** Public: fetch a CMS page by key (no auth) with a 2-second timeout. */
export async function fetchCmsPage(pageKey) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 2000);

  try {
    const response = await fetch(buildApiUrl(`/api/cms/pages/${pageKey}`), {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    const result = await parseJsonResponse(response);
    if (!response.ok || !result.ok) {
      throw new Error(result.message || "CMS page fetch failed");
    }
    return result;
  } catch (err) {
    clearTimeout(timeoutId);
    throw err;
  }
}

/** Admin: login and store JWT. */
export async function cmsLogin(username, password) {
  const response = await fetch(buildApiUrl("/api/cms/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const result = await parseJsonResponse(response);
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Login failed");
  }
  setCmsToken(result.token);
  return result;
}

export async function cmsLogout() {
  setCmsToken(null);
}

export async function cmsAuthFetch(path, options = {}) {
  const token = getCmsToken();
  const headers = {
    ...(options.headers || {}),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (options.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(buildApiUrl(path), { ...options, headers });
  const result = await parseJsonResponse(response);

  if (response.status === 401) {
    setCmsToken(null);
    // Stale/broken sessions — send the editor back to login.
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      window.location.assign("/login");
    }
  }

  return { response, result };
}

export async function fetchCmsPagesAdmin() {
  const { response, result } = await cmsAuthFetch("/api/cms/pages");
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Failed to load pages");
  }
  return result.pages;
}

export async function saveCmsSection(pageKey, sectionKey, content, sortOrder = 0, changeSummary = "") {
  const { response, result } = await cmsAuthFetch(
    `/api/cms/pages/${pageKey}/sections/${sectionKey}`,
    {
      method: "PUT",
      body: JSON.stringify({ content, sortOrder, changeSummary }),
    }
  );
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Failed to save section");
  }
  return result;
}

/** Per-page version history (scoped to one page, not site-wide). */
export async function fetchCmsRevisions(pageKey, { limit = 50, offset = 0 } = {}) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const { response, result } = await cmsAuthFetch(
    `/api/cms/pages/${pageKey}/revisions?${params}`
  );
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Failed to load version history");
  }
  return result;
}

export async function fetchCmsRevision(pageKey, revisionId) {
  const { response, result } = await cmsAuthFetch(
    `/api/cms/pages/${pageKey}/revisions/${revisionId}`
  );
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Failed to load revision");
  }
  return result.revision;
}

export async function revertCmsRevision(pageKey, revisionId) {
  const { response, result } = await cmsAuthFetch(
    `/api/cms/pages/${pageKey}/revisions/${revisionId}/revert`,
    { method: "POST" }
  );
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Failed to restore version");
  }
  return result;
}

export async function uploadCmsMedia(file, mediaKey = "", altText = "") {
  const token = getCmsToken();
  const formData = new FormData();
  formData.append("file", file);
  if (mediaKey) formData.append("mediaKey", mediaKey);
  if (altText) formData.append("altText", altText);

  const response = await fetch(buildApiUrl("/api/cms/media/upload"), {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const result = await parseJsonResponse(response);
  if (!response.ok || !result.ok) {
    throw new Error(result.message || "Upload failed");
  }
  return result.media;
}
